import { useState, useEffect } from 'react'
import { Chip, IconButton, Tooltip, Box } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import Swal from 'sweetalert2'
import EditBannerModal from '../../EditBannerModal'
import { useAppServices } from 'hook/services'

const AppData = () => {
  const [banners, setBanners] = useState([])
  const [loader, setLoader] = useState(false)
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] })
  const AppService = useAppServices()

  const fetchBanners = async () => {
    setLoader(true)
    try {
      const { response } = await AppService.banner.get()
      setBanners(response.banners || [])
    } catch (err) {
      console.error('Failed to fetch banners', err)
    } finally {
      setLoader(false)
    }
  }

  const handleToggleActive = async (banner) => {
    try {
      const payload = { id: banner._id, isActive: !banner.isActive }
      await AppService.banner.update({ payload })
      fetchBanners()
    } catch (err) {
      Swal.fire('Error', 'Failed to update banner status', 'error')
    }
  }

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Banner?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = { id }
          await AppService.banner.delete({ payload })
          Swal.fire('Deleted!', 'Banner has been deleted.', 'success')
          fetchBanners()
        } catch (err) {
          Swal.fire('Error', 'Failed to delete banner', 'error')
        }
      }
    })
  }

  const handleRefresh = () => fetchBanners()

  useEffect(() => {
    fetchBanners()
  }, [])

  useEffect(() => {
    const columns = [
      { Header: 'Order', accessor: 'order', width: '6%' },
      { Header: 'Title', accessor: 'title', width: '18%' },
      { Header: 'Desktop Image', accessor: 'desktopImage', width: '22%' },
            { Header: 'Mobile Image', accessor: 'mobileImage', width: '22%' },

      { Header: 'Status', accessor: 'status', width: '10%' },
      { Header: 'Actions', accessor: 'actions', width: '22%', disableSortBy: true },
    ]

    const rows = [...banners]
      .sort((a, b) => a.order - b.order)
      .map((banner) => ({
        order: (
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#555' }}>
            {banner.order}
          </span>
        ),
        title: (
          <Tooltip title={banner.title || '—'}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {banner.title || <span style={{ color: '#aaa' }}>Untitled</span>}
            </span>
          </Tooltip>
        ),
        desktopImage: (
          <Box display="flex" alignItems="center" gap={1}>
            {banner.desktopImage ? (
              <>
                <Box
                  component="img"
                  src={`${banner.desktopImage}?width=120&quality=70`}
                  alt="desktop"
                  sx={{ width: 80, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none' }}
                />

              </>
            ) : (
              <span style={{ color: '#aaa', fontSize: '0.8rem' }}>—</span>
            )}
          </Box>
        ),
       
        mobileImage: (
          <Box display="flex" alignItems="center" gap={1}>
            {banner.mobileImage ? (
              <>
                <Box
                  component="img"
                  src={`${banner.mobileImage}?width=120&quality=70`}
                  alt="mobile"
                  sx={{ width: 60, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none' }}
                />

              </>
            ) : (
              <span style={{ color: '#aaa', fontSize: '0.8rem' }}>—</span>
            )}
          </Box>
        ),
        status: (
          <Chip
            label={banner.isActive ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              backgroundColor: banner.isActive ? '#e8f5e9' : '#fff3e0',
              color: banner.isActive ? '#2e7d32' : '#e65100',
              fontWeight: 600,
              fontSize: '0.75rem',
              border: `1px solid ${banner.isActive ? '#a5d6a7' : '#ffcc80'}`,
            }}
          />
        ),
        actions: (
          <Box display="flex" gap={0.5} alignItems="center">
            <EditBannerModal banner={banner} handleRefresh={handleRefresh} />

            <Tooltip title={banner.isActive ? 'Deactivate' : 'Activate'}>
              <IconButton
                size="small"
                onClick={() => handleToggleActive(banner)}
                sx={{
                  color: banner.isActive ? '#e65100' : '#2e7d32',
                  '&:hover': { backgroundColor: banner.isActive ? '#fff3e0' : '#e8f5e9' },
                }}
              >
                {banner.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(banner._id)}
                sx={{ color: '#c62828', '&:hover': { backgroundColor: '#ffebee' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      }))

    setDataTableData({ columns, rows })
  }, [banners])

  return {
    dataTableData,
    handleRefresh,
    loader,
  }
}

export default AppData